import { z } from "zod";

const PaymentType = z.enum(["INSURED", "CASH"]);
const CategoryTypeType = z.enum(["SECTION", "FEATURED", "TAB"]);

const HrtCountType = z.object({
  __typename: z.literal("HrtCount"),
  activeProviderGroups: z.number(),
});

export const Hrt = z.object({
  hrtId: z.number(),
  name: z.string(),
  displayName: z.string(),
  description: z.string().nullish(),
  isActive: z.boolean().optional(),
  _count: HrtCountType,
  displayMetadata: z
    .object({
      description: z.array(
        z.object({
          type: z.string(),
          value: z.string(),
        }),
      ),
      ingredients: z.any(),
      imageUrl: z.string(),
      showContinueWithoutSchedulingButton: z.boolean().optional(),
      // Number of days out the earliest bookable appointment is for this HRT.
      // Overrides the default booking lead time (see DEFAULT_BOOKING_LEAD_TIME_DAYS).
      // e.g. 1 => this HRT can be scheduled 1 day out instead of the default 2.
      overrideBookingTimeDays: z.number().int().min(0).optional(),
      // Copy rendered under the "Continue without scheduling" CTA on the
      // schedule screen, e.g. "This provider does not always schedule for this
      // treatment." Read by getScheduleScreenTexts in the schedule utils; see
      // plans/hrt-schedule-screen-metadata.md for the ops-facing reference.
      //
      // `text` is rendered verbatim for every locale — this copy is not
      // localized, unlike description/ingredients.
      //
      // `color`/`size`/`weight` are named tokens, NOT Tailwind classes. The
      // reader maps them to classes, and an unrecognized value falls back to
      // the default rather than rendering unstyled. Omit all three to keep the
      // original look (xs, gray, inherited weight).
      scheduleScreen: z
        .object({
          text: z.string().optional(),
          color: z.enum(["gray", "black", "red"]).optional(),
          size: z.enum(["xs", "sm", "base", "lg"]).optional(),
          weight: z.enum(["normal", "medium", "semibold", "bold"]).optional(),
        })
        .optional(),
    })
    .optional(),
  isValidGivenCart: z.boolean().default(true).optional(), //slice of state we will use to determine if a HRT is valid given the current cart state
});

export type Hrt = z.infer<typeof Hrt>;

const HrtPaymentType = z.object({
  __typename: z.literal("HrtPaymentType"),
  paymentType: PaymentType,
  id: z.number(),
});

const CashPrice = z.object({
  __typename: z.literal("CashPrice"),
  cashPrice: z.number(),
});

const ProviderGroup = z.object({
  __typename: z.literal("ProviderGroup"),
  practiceId: z.number(),
  providerGroupId: z.number(),
});

const ProviderGroupToHrt = z.object({
  __typename: z.literal("ProviderGroupToHrt"),
  isActive: z.boolean(),
  providerGroup: ProviderGroup,
  notBookable: z.boolean().optional(),
});

/**
 * Extension of Hrt object to contain other properties from GraphQL query
 */
export enum KioskHrtType {
  DEFAULT_KIOSK = "DEFAULT_KIOSK",
  EPS = "EPS",
  WORKERS_COMP = "WORKERS_COMP",
}

export const HrtExtended = Hrt.extend({
  __typename: z.literal("Hrt"),
  popularity: z.number(),
  isConsumerFacing: z.boolean(),
  hrtPaymentType: z.array(HrtPaymentType),
  cash_prices: CashPrice.nullish(),
  providerGroupToHrt: z.array(ProviderGroupToHrt),
  ehrPrimaryAppointmentTypeId: z.array(z.string()),
  isBookableOnStorefront: z.boolean().optional(),
  HrtFlags: z.array(
    z.object({
      isVirtualVisit: z.boolean().optional(),
      kioskHrtType: z
        .enum([
          KioskHrtType.DEFAULT_KIOSK,
          KioskHrtType.EPS,
          KioskHrtType.WORKERS_COMP,
        ])
        .nullish(),
    }),
  ),
  HrtTranslation: z
    .array(
      z.object({
        languageCode: z.string(),
        name: z.string().optional(),
        displayName: z.string().optional(),
        displayMetadata: z.any().optional(),
      }),
    )
    .optional(),
});

const ProductToHrt = z.object({
  __typename: z.literal("ProductToHrt"),
  hrtId: z.number(),
  hrt: HrtExtended,
  product_component_type: z.string(),
});

const ProductType = z.object({
  __typename: z.literal("Products"),
  categoryIds: z.array(z.number()),
  name: z.string(),
  id: z.number(),
  productToHrt: z.array(ProductToHrt),
  product_page_type: z.string(),
  popularity: z.number().optional(),
  displayMetadata: z
    .object({
      description: z.array(
        z.object({
          type: z.string(),
          value: z.string(),
        }),
      ),
      "extension-tag": z.string().optional(),
      overview: z.any(),
      ingredients: z.any(),
      imageUrl: z.string(),
      packageIncludes: z.any(),
      productLink: z.string().optional(),
      marketingCopy: z.string().optional(),
      preDiscount: z.string().optional(),
      banner: z
        .object({
          title: z.string(),
          subtext: z.string(),
          description: z.string(),
          proceedText: z.string(),
        })
        .optional(),
    })
    .optional(),
});

const CategoryType = z.object({
  id: z.number(),
  orgId: z.number(),
  name: z.string(),
  displayMetadata: z.object({}).nullish(),
  __typename: z.literal("ProductCategories"),
  type: CategoryTypeType,
  products: z.array(ProductType),
});

export type ProductType = z.infer<typeof ProductType>;
export type ProductToHrtType = z.infer<typeof ProductToHrt>;

export type CategoryType = z.infer<typeof CategoryType>;

// Using the extended Hrt type here
export type HrtExtended = z.infer<typeof HrtExtended>;
